import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'motion/react';
import { useNavigate } from 'react-router-dom';
import { FiSearch, FiArrowRight } from 'react-icons/fi';
import { HiOutlineLightBulb } from 'react-icons/hi';
import { HiSparkles } from 'react-icons/hi2';

import ClientNavbar from '../components/Navbar/ClinetNavbar';
import ClientBanner from '../components/Client/ClientBanner';
import ClientReels from '../components/Client/ClientReels';
import ClientHomeCategory from '../components/Client/ClientHomeCategory';
import FixedButton from '../components/Client/FixedButton';
import ClientFooter from '../components/Client/ClientFooter';
import ClientCategoryList from '../components/Client/ClientCategoryList';
import ClientAdSense from '../components/Client/ClientAdSense';

const heroContainer = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.12, delayChildren: 0.1 } },
};

const heroItem = {
    hidden: { y: 30, opacity: 0 },
    visible: {
        y: 0,
        opacity: 1,
        transition: { type: 'spring', stiffness: 110, damping: 18 },
    },
};

const ROTATING_WORDS = ['AI Image', 'AI Art', 'Avatar', 'Cinematic', 'Creative'];

const AI_TOOLS = ['Midjourney', 'DALL·E', 'Stable Diffusion', 'Leonardo', 'Flux', 'Sora', 'Runway', 'Ideogram'];

const RotatingWord = () => {
    const [idx, setIdx] = useState(0);
    useEffect(() => {
        const t = setInterval(
            () => setIdx((i) => (i + 1) % ROTATING_WORDS.length),
            2400
        );
        return () => clearInterval(t);
    }, []);
    return (
        <span
            style={{
                display: 'inline-block',
                position: 'relative',
                verticalAlign: 'baseline',
            }}
        >
            <AnimatePresence mode='wait'>
                <motion.span
                    key={idx}
                    initial={{ y: '0.55em', opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: '-0.55em', opacity: 0 }}
                    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    style={{
                        display: 'inline-block',
                        background: 'linear-gradient(90deg, #FCFA02, #FF8AFF, #7F00FF)',
                        backgroundSize: '200% 100%',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        animation: 'heroShine 3s linear infinite',
                    }}
                >
                    {ROTATING_WORDS[idx]}
                </motion.span>
            </AnimatePresence>
        </span>
    );
};

const StatCounter = ({ end, suffix = '', label, delay = 0 }) => {
    const [value, setValue] = useState(0);
    useEffect(() => {
        const startDelay = setTimeout(() => {
            const duration = 1400;
            const steps = 50;
            const step = end / steps;
            let current = 0;
            const interval = setInterval(() => {
                current += step;
                if (current >= end) {
                    setValue(end);
                    clearInterval(interval);
                } else {
                    setValue(Math.floor(current));
                }
            }, duration / steps);
            return () => clearInterval(interval);
        }, delay);
        return () => clearTimeout(startDelay);
    }, [end, delay]);
    return (
        <div className='flex flex-col items-center min-w-0'>
            <div className='text-xl sm:text-2xl md:text-4xl font-bold text-client-font tabular-nums'>
                {value}
                {suffix}
            </div>
            <div className='text-[10px] sm:text-xs md:text-sm text-client-font/55 mt-1 text-center'>
                {label}
            </div>
        </div>
    );
};

const ClientHome = () => {
    const controller = useSelector((state) => state?.controller?.controller);
    const navigate = useNavigate();
    const [searchVal, setSearchVal] = useState('');

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        navigate('/search');
    };

    return (
        <div className='min-h-screen dm-sans client-home-bg'>
            <div className='w-full'>
                <ClientNavbar />
                <FixedButton />

                <section
                    className='relative w-full pt-6 pb-10 md:pt-10 md:pb-16'
                    style={{ overflow: 'clip', maxWidth: '100%' }}
                >
                    {/* Grid pattern with radial fade mask */}
                    <div
                        aria-hidden
                        className='absolute inset-0 pointer-events-none'
                        style={{
                            backgroundImage:
                                'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)',
                            backgroundSize: '44px 44px',
                            WebkitMaskImage:
                                'radial-gradient(ellipse 70% 60% at 50% 40%, black 20%, transparent 80%)',
                            maskImage:
                                'radial-gradient(ellipse 70% 60% at 50% 40%, black 20%, transparent 80%)',
                            zIndex: 0,
                        }}
                    />

                    {/* Floating gradient orbs — static (animating a blurred layer is a major
                        repaint cost on mobile; the drift was barely perceptible) */}
                    <div
                        aria-hidden
                        className='pointer-events-none absolute top-10 left-0 w-48 h-48 sm:w-64 sm:h-64 rounded-full opacity-20 blur-2xl'
                        style={{
                            background: 'radial-gradient(circle, #7F00FF 0%, transparent 70%)',
                            zIndex: 0,
                        }}
                    />
                    <div
                        aria-hidden
                        className='pointer-events-none absolute bottom-0 right-0 w-48 h-48 sm:w-64 sm:h-64 rounded-full opacity-15 blur-2xl'
                        style={{
                            background: 'radial-gradient(circle, #FCFA02 0%, transparent 70%)',
                            zIndex: 0,
                        }}
                    />

                    {/* Main content */}
                    <motion.div
                        variants={heroContainer}
                        initial='hidden'
                        animate='visible'
                        className='relative w-full max-w-5xl mx-auto flex flex-col items-center text-center px-4 sm:px-6 gap-4 md:gap-6'
                        style={{ zIndex: 1 }}
                    >
                        {/* Eyebrow badge */}
                        <motion.div variants={heroItem}>
                            <div className='ios-glass inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-[11px] sm:text-xs md:text-sm text-client-font/90'>
                                <motion.span
                                    animate={{ rotate: [0, 25, -25, 0], scale: [1, 1.25, 1] }}
                                    transition={{
                                        duration: 2.5,
                                        repeat: Infinity,
                                        ease: 'easeInOut',
                                    }}
                                    style={{ display: 'inline-flex', color: '#FCFA02' }}
                                >
                                    <HiSparkles />
                                </motion.span>
                                <span className='tracking-wide'>AI-Powered Prompt Library</span>
                            </div>
                        </motion.div>

                        {/* Headline */}
                        <motion.h1
                            variants={heroItem}
                            className='text-[30px] sm:text-[40px] md:text-[58px] lg:text-[68px] font-bold text-client-font leading-[1.08] tracking-tight max-w-4xl'
                        >
                            The ultimate platform for{' '}
                            <RotatingWord />
                            <span className='block sm:inline'> Prompts</span>
                        </motion.h1>

                        {/* Subtitle */}
                        <motion.p
                            variants={heroItem}
                            className='text-xs sm:text-sm md:text-lg text-client-font/65 max-w-2xl'
                        >
                            Work smarter, not harder. Generate stunning visuals with
                            ready-made prompts crafted for the world's best AI image tools.
                        </motion.p>

                        {/* CTA row */}
                        <motion.form
                            variants={heroItem}
                            onSubmit={handleSearchSubmit}
                            className='w-full max-w-2xl flex flex-col sm:flex-row items-stretch gap-2 mt-1'
                        >
                            {/* Search input */}
                            <div className='relative flex-1 group'>
                                <FiSearch className='absolute left-4 top-1/2 -translate-y-1/2 text-client-font/60 text-base md:text-lg pointer-events-none z-10' />
                                <input
                                    type='text'
                                    value={searchVal}
                                    onChange={(e) => setSearchVal(e.target.value)}
                                    onClick={() => navigate('/search')}
                                    placeholder='Search "cyberpunk portrait"...'
                                    className='ios-glass w-full pl-11 pr-4 py-3 md:py-3.5 rounded-full text-client-font placeholder:text-client-font/40 focus:outline-none focus:border-client-primary text-sm md:text-base cursor-pointer transition-all'
                                />
                                <div
                                    className='absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-300'
                                    style={{
                                        boxShadow: '0 0 30px rgba(127,0,255,0.35)',
                                    }}
                                />
                            </div>

                            {/* ===== MOBILE: segmented action bar (Explore + lightbulb in one pill) ===== */}
                            <div
                                className='sm:hidden flex items-stretch rounded-full overflow-hidden'
                                style={{
                                    boxShadow:
                                        '0 10px 28px -8px rgba(252,250,2,0.35), inset 0 1px 0 rgba(255,255,255,0.45)',
                                }}
                            >
                                <motion.button
                                    type='submit'
                                    whileTap={{ scale: 0.97 }}
                                    className='flex-1 px-5 py-3 inline-flex items-center justify-center gap-2 text-sm font-bold uppercase tracking-wider relative overflow-hidden'
                                    style={{
                                        background:
                                            'linear-gradient(135deg, #FFF06F 0%, #FCFA02 50%, #FFD600 100%)',
                                        color: '#0a0a0a',
                                    }}
                                >
                                    <span className='relative'>Explore</span>
                                    <FiArrowRight className='relative text-base' />
                                </motion.button>
                                <div
                                    className='w-px self-stretch'
                                    style={{ background: 'rgba(0,0,0,0.15)' }}
                                />
                                <motion.button
                                    type='button'
                                    onClick={() => navigate('/suggestion/send')}
                                    whileTap={{ scale: 0.92 }}
                                    aria-label='Send suggestion'
                                    className='relative px-4 py-3 inline-flex items-center justify-center gap-1.5 overflow-hidden text-white'
                                    style={{
                                        background:
                                            'linear-gradient(135deg, #7F00FF 0%, #FF8AFF 60%, #7F00FF 100%)',
                                    }}
                                >
                                    <div className='relative'>
                                        <HiOutlineLightBulb
                                            className='text-lg'
                                            style={{
                                                filter:
                                                    'drop-shadow(0 0 6px rgba(255,255,255,0.7))',
                                            }}
                                        />
                                    </div>
                                    <span className='relative text-[10px] font-bold uppercase tracking-wider'>
                                        Idea
                                    </span>
                                </motion.button>
                            </div>

                            {/* ===== DESKTOP: separate Explore + lightbulb buttons (sm+) ===== */}
                            <motion.button
                                type='submit'
                                whileHover={{ scale: 1.03 }}
                                whileTap={{ scale: 0.97 }}
                                className='hidden sm:inline-flex px-5 md:px-6 py-3 md:py-3.5 rounded-full text-sm md:text-base font-semibold items-center justify-center gap-2 whitespace-nowrap'
                                style={{
                                    background:
                                        'linear-gradient(180deg, #FFF06F 0%, #FCFA02 100%)',
                                    color: '#0a0a0a',
                                    boxShadow:
                                        '0 8px 24px -4px rgba(252,250,2,0.35), inset 0 1px 0 0 rgba(255,255,255,0.55), inset 0 -1px 0 0 rgba(0,0,0,0.08)',
                                }}
                            >
                                Explore <FiArrowRight />
                            </motion.button>

                            <motion.button
                                type='button'
                                onClick={() => navigate('/suggestion/send')}
                                whileHover={{ scale: 1.06, y: -2 }}
                                whileTap={{ scale: 0.94 }}
                                aria-label='Send suggestion'
                                className='hidden sm:inline-flex relative px-4 md:px-5 py-3 md:py-3.5 rounded-full text-white items-center justify-center gap-1.5 overflow-hidden group whitespace-nowrap'
                                style={{
                                    background:
                                        'linear-gradient(135deg, #7F00FF 0%, #FF8AFF 50%, #7F00FF 100%)',
                                    boxShadow:
                                        '0 10px 28px -6px rgba(127,0,255,0.55), inset 0 1px 0 rgba(255,255,255,0.28)',
                                }}
                            >
                                {/* Pulsing halo */}
                                <motion.span
                                    aria-hidden
                                    animate={{
                                        scale: [1, 1.5, 1],
                                        opacity: [0.55, 0, 0.55],
                                    }}
                                    transition={{ duration: 1.8, repeat: Infinity }}
                                    className='absolute w-10 h-10 rounded-full pointer-events-none'
                                    style={{
                                        background:
                                            'radial-gradient(circle, rgba(255,138,255,0.55), transparent 70%)',
                                    }}
                                />
                                {/* Sweep shine on hover */}
                                <motion.div
                                    aria-hidden
                                    className='absolute inset-y-0 w-1/2 pointer-events-none opacity-0 group-hover:opacity-100 group-hover:left-full transition-all duration-700 -left-1/2'
                                    style={{
                                        background:
                                            'linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.55) 50%, transparent 70%)',
                                        filter: 'blur(2px)',
                                    }}
                                />
                                <motion.div
                                    animate={{
                                        rotate: [0, 8, -8, 0],
                                        y: [0, -1, 0],
                                    }}
                                    transition={{
                                        duration: 2,
                                        repeat: Infinity,
                                        ease: 'easeInOut',
                                    }}
                                    className='relative'
                                >
                                    <HiOutlineLightBulb
                                        className='text-lg md:text-xl'
                                        style={{
                                            filter:
                                                'drop-shadow(0 0 6px rgba(255,255,255,0.75))',
                                        }}
                                    />
                                </motion.div>
                                <span className='relative text-xs md:text-sm font-semibold tracking-wide hidden md:inline'>
                                    Idea
                                </span>
                            </motion.button>
                        </motion.form>

                        {/* Stats */}
                        <motion.div
                            variants={heroItem}
                            className='grid grid-cols-3 gap-4 sm:gap-8 md:gap-14 mt-4 md:mt-6 w-full max-w-2xl'
                        >
                            <StatCounter end={100} suffix='K+' label='Active Users' delay={300} />
                            <StatCounter end={50} suffix='K+' label='Prompts' delay={450} />
                            <StatCounter end={25} suffix='+' label='AI Models' delay={600} />
                        </motion.div>

                        {/* Categories pills */}
                        <motion.div variants={heroItem} className='w-full mt-2'>
                            <ClientCategoryList showRandom={true} />
                        </motion.div>
                    </motion.div>

                    {/* Trusted-by marquee */}
                    <div className='relative mt-8 md:mt-12' style={{ zIndex: 1 }}>
                        <p className='text-center text-[10px] md:text-[11px] text-client-font/40 mb-3 tracking-[0.25em] uppercase px-4'>
                            Works with leading AI tools
                        </p>
                        <div className='overflow-hidden relative w-full'>
                            <div className='marquee-track flex gap-8 md:gap-14 whitespace-nowrap text-client-font/45 text-sm md:text-lg font-semibold'>
                                {[...Array(2)].map((_, group) => (
                                    <div
                                        key={group}
                                        className='flex gap-8 md:gap-14 whitespace-nowrap shrink-0'
                                        aria-hidden={group === 1 ? true : undefined}
                                    >
                                        {AI_TOOLS.map((tool, i) => (
                                            <React.Fragment key={`${group}-${i}`}>
                                                <span>{tool}</span>
                                                <span className='text-client-font/25'>•</span>
                                            </React.Fragment>
                                        ))}
                                    </div>
                                ))}
                            </div>
                            <div
                                className='absolute inset-y-0 left-0 w-20 md:w-32 pointer-events-none'
                                style={{
                                    background:
                                        'linear-gradient(90deg, #0a0a0a 20%, transparent)',
                                }}
                            />
                            <div
                                className='absolute inset-y-0 right-0 w-20 md:w-32 pointer-events-none'
                                style={{
                                    background:
                                        'linear-gradient(-90deg, #0a0a0a 20%, transparent)',
                                }}
                            />
                        </div>
                    </div>

                    <style>{`
                        @keyframes heroShine {
                            0% { background-position: 0% 50%; }
                            100% { background-position: 200% 50%; }
                        }
                        @keyframes marqueeScroll {
                            0% { transform: translateX(0); }
                            100% { transform: translateX(-50%); }
                        }
                        .marquee-track {
                            animation: marqueeScroll 28s linear infinite;
                            width: max-content;
                        }
                    `}</style>
                </section>

                {/* DSPY1 — top banner ad (auto-collapses if unfilled) */}
                <ClientAdSense
                    slot='9729451986'
                    containerClassName='w-full max-w-5xl mx-auto px-4 sm:px-6 mb-4 md:mb-6'
                />

                {controller?.banner_status && <ClientBanner />}
                {controller?.story_status && <ClientReels />}
                <ClientHomeCategory />
                <ClientFooter />
            </div>
        </div>
    );
};

export default ClientHome;
